export type Point2D = { x: number; y: number };

export type NormalizedPoint = { x: number; y: number };

export type FlightSegmentKind =
  | 'approach'
  | 'ascend'
  | 'descend'
  | 'pan_left'
  | 'pan_right'
  | 'dolly_in'
  | 'dolly_out'
  | 'orbit_left'
  | 'orbit_right'
  | 'forward';

export type FlightSpeed = 'slow' | 'medium' | 'fast';

export type FlightSegment = {
  direction: FlightSegmentKind;
  percentage: number;
  speed: FlightSpeed;
  startNorm: NormalizedPoint;
  endNorm: NormalizedPoint;
  lengthPx: number;
};

export type TimedWaypoint = {
  /** 0–1 along total path length */
  t: number;
  /** seconds from video start */
  timeSec: number;
  /** normalized image coords 0–1 */
  x: number;
  y: number;
  /** screen percent integers */
  xPct: number;
  yPct: number;
};

/**
 * Dense polyline sampled along the drawn path, timed to `durationSeconds`.
 * These coordinates are the immutable flight plan for Kling prompts.
 */
export function sampleTimedWaypoints(
  points: Point2D[],
  canvasSize: { width: number; height: number },
  durationSeconds: number,
  approachPoint?: Point2D | null,
  maxSamples = 12,
  options?: { preserveStroke?: boolean },
): TimedWaypoint[] {
  const { width, height } = canvasSize;
  if (width <= 0 || height <= 0 || points.length < 2) return [];

  const chain: Point2D[] = [];
  if (approachPoint) chain.push(approachPoint);
  if (options?.preserveStroke) {
    chain.push(...densifyStrokeForExport(points, 2));
  } else {
    chain.push(...simplifyPath(points, Math.max(3, Math.min(width, height) * 0.008)));
  }
  if (chain.length < 2) return [];

  const segLens: number[] = [];
  let total = 0;
  for (let i = 1; i < chain.length; i++) {
    const len = dist(chain[i - 1], chain[i]);
    segLens.push(len);
    total += len;
  }
  if (total <= 0) return [];

  const dur = durationSeconds <= 5 ? 5 : 10;
  const sampleCount = Math.max(2, Math.min(maxSamples, chain.length + 2));
  const result: TimedWaypoint[] = [];

  for (let s = 0; s < sampleCount; s++) {
    const u = s / (sampleCount - 1);
    const target = u * total;
    let acc = 0;
    let pt = chain[chain.length - 1];
    for (let i = 0; i < segLens.length; i++) {
      if (acc + segLens[i] >= target || i === segLens.length - 1) {
        const local = segLens[i] > 0 ? (target - acc) / segLens[i] : 1;
        const a = chain[i];
        const b = chain[i + 1];
        pt = {
          x: a.x + (b.x - a.x) * Math.max(0, Math.min(1, local)),
          y: a.y + (b.y - a.y) * Math.max(0, Math.min(1, local)),
        };
        break;
      }
      acc += segLens[i];
    }
    const norm = normalizePoint(pt, width, height);
    result.push({
      t: u,
      timeSec: Math.round(u * dur * 10) / 10,
      x: norm.x,
      y: norm.y,
      xPct: Math.round(norm.x * 100),
      yPct: Math.round(norm.y * 100),
    });
  }
  return result;
}

/** Compass-ish bearing label from normalized delta (image y grows downward). */
export function bearingLabel(dx: number, dy: number): string {
  const angle = Math.atan2(-dy, dx) * (180 / Math.PI); // screen → math y-up
  const abs = ((angle % 360) + 360) % 360;
  if (abs >= 337.5 || abs < 22.5) return 'RIGHT';
  if (abs < 67.5) return 'UP-RIGHT';
  if (abs < 112.5) return 'UP';
  if (abs < 157.5) return 'UP-LEFT';
  if (abs < 202.5) return 'LEFT';
  if (abs < 247.5) return 'DOWN-LEFT';
  if (abs < 292.5) return 'DOWN';
  return 'DOWN-RIGHT';
}

export type FlightPathAnalysis = {
  waypoints: Array<Point2D & { norm: NormalizedPoint }>;
  segments: FlightSegment[];
  totalLengthPx: number;
  canvasSize: { width: number; height: number };
};

const MAX_SEGMENTS = 6;
const MIN_SEGMENT_FRACTION = 0.06;

/** Kling multi-shot allows at most 6 shots; each shot needs ≥1s, so cap by duration too. */
export function maxSegmentsForDuration(durationSeconds: number): number {
  const dur = durationSeconds <= 5 ? 5 : 10;
  return Math.min(MAX_SEGMENTS, Math.max(2, dur));
}

function dist(a: Point2D, b: Point2D): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function normalizePoint(p: Point2D, width: number, height: number): NormalizedPoint {
  return {
    x: Math.max(0, Math.min(1, p.x / width)),
    y: Math.max(0, Math.min(1, p.y / height)),
  };
}

/** Douglas-Peucker path simplification to reduce mouse jitter. */
export function simplifyPath(points: Point2D[], tolerance = 4): Point2D[] {
  if (points.length <= 2) return points;

  const perpendicularDistance = (point: Point2D, lineStart: Point2D, lineEnd: Point2D) => {
    const dx = lineEnd.x - lineStart.x;
    const dy = lineEnd.y - lineStart.y;
    if (dx === 0 && dy === 0) return dist(point, lineStart);
    const t = ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / (dx * dx + dy * dy);
    const clamped = Math.max(0, Math.min(1, t));
    const proj = { x: lineStart.x + clamped * dx, y: lineStart.y + clamped * dy };
    return dist(point, proj);
  };

  const simplify = (pts: Point2D[], epsilon: number): Point2D[] => {
    if (pts.length <= 2) return pts;
    let maxDist = 0;
    let index = 0;
    for (let i = 1; i < pts.length - 1; i++) {
      const d = perpendicularDistance(pts[i], pts[0], pts[pts.length - 1]);
      if (d > maxDist) {
        maxDist = d;
        index = i;
      }
    }
    if (maxDist > epsilon) {
      const left = simplify(pts.slice(0, index + 1), epsilon);
      const right = simplify(pts.slice(index), epsilon);
      return left.slice(0, -1).concat(right);
    }
    return [pts[0], pts[pts.length - 1]];
  };

  return simplify(points, tolerance);
}

function classifySegment(
  start: NormalizedPoint,
  end: NormalizedPoint,
  dx: number,
  dy: number,
): FlightSegmentKind {
  const center = { x: 0.5, y: 0.5 };
  const startDist = Math.hypot(start.x - center.x, start.y - center.y);
  const endDist = Math.hypot(end.x - center.x, end.y - center.y);
  const radialDelta = endDist - startDist;

  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);
  const total = Math.hypot(absDx, absDy) || 1;

  // Strong radial movement = dolly in/out (depth)
  if (Math.abs(radialDelta) > 0.04 && Math.abs(radialDelta) * total > absDx * 0.35 && Math.abs(radialDelta) * total > absDy * 0.35) {
    return radialDelta < 0 ? 'dolly_in' : 'dolly_out';
  }

  if (absDy > absDx * 1.25) {
    return dy < 0 ? 'ascend' : 'descend';
  }

  if (absDx > absDy * 1.25) {
    return dx > 0 ? 'pan_right' : 'pan_left';
  }

  // Diagonal along facade = lateral track (not "orbit around" — that cue often reverses screen direction)
  if (dy < 0 && dx > 0) return 'pan_right';
  if (dy < 0 && dx < 0) return 'pan_left';
  if (dy > 0 && dx > 0) return 'pan_right';
  if (dy > 0 && dx < 0) return 'pan_left';

  return radialDelta < 0 ? 'dolly_in' : 'forward';
}

/**
 * Pace for the WHOLE route so S→E always completes in `durationSeconds`.
 * Longer path / shorter time → faster; never used to truncate geometry.
 */
export function paceForPathDuration(
  totalLengthPx: number,
  canvasSize: { width: number; height: number },
  durationSeconds: number,
): FlightSpeed {
  const { width, height } = canvasSize;
  const diagonal = Math.hypot(width, height) || 1;
  const dur = durationSeconds <= 5 ? 5 : 10;
  const diagonalsPerSecond = totalLengthPx / diagonal / dur;
  // ~0.08 diag/s ≈ gentle cruise; ≥0.22 ≈ must rush to finish
  if (diagonalsPerSecond >= 0.22) return 'fast';
  if (diagonalsPerSecond <= 0.08) return 'slow';
  return 'medium';
}

function speedForSegment(
  length: number,
  avgLength: number,
  globalPace: FlightSpeed,
): FlightSpeed {
  // Global duration pace is primary; only nudge extreme outliers one step
  const order: FlightSpeed[] = ['slow', 'medium', 'fast'];
  const baseIdx = order.indexOf(globalPace);
  if (avgLength <= 0) return globalPace;
  const ratio = length / avgLength;
  if (ratio > 1.6 && baseIdx < 2) return order[baseIdx + 1];
  if (ratio < 0.45 && baseIdx > 0) return order[baseIdx - 1];
  return globalPace;
}

type RawSegment = {
  direction: FlightSegmentKind;
  length: number;
  startNorm: NormalizedPoint;
  endNorm: NormalizedPoint;
};

function mergeSmallSegments(raw: RawSegment[], totalLength: number): RawSegment[] {
  const merged: RawSegment[] = [];
  for (const seg of raw) {
    if (seg.length / totalLength < MIN_SEGMENT_FRACTION && merged.length > 0) {
      const prev = merged[merged.length - 1];
      prev.length += seg.length;
      prev.endNorm = seg.endNorm;
    } else {
      merged.push({ ...seg });
    }
  }
  return merged;
}

/** Merge shortest adjacent pairs until under max — preserves major direction changes. */
function capSegments(raw: RawSegment[], maxSegments = MAX_SEGMENTS): RawSegment[] {
  let segs = raw.map((s) => ({ ...s }));
  while (segs.length > maxSegments) {
    let minIdx = 0;
    let minLen = Infinity;
    for (let i = 0; i < segs.length - 1; i++) {
      const combined = segs[i].length + segs[i + 1].length;
      if (combined < minLen) {
        minLen = combined;
        minIdx = i;
      }
    }
    const a = segs[minIdx];
    const b = segs[minIdx + 1];
    segs.splice(minIdx, 2, {
      direction: a.length >= b.length ? a.direction : b.direction,
      length: a.length + b.length,
      startNorm: a.startNorm,
      endNorm: b.endNorm,
    });
  }
  return segs;
}

function toWeightedSegments(
  raw: RawSegment[],
  canvasSize: { width: number; height: number },
  durationSeconds: number,
): FlightSegment[] {
  const total = raw.reduce((s, e) => s + e.length, 0) || 1;
  const avg = total / raw.length;
  const globalPace = paceForPathDuration(total, canvasSize, durationSeconds);
  const segments = raw.map((seg) => ({
    direction: seg.direction,
    percentage: seg.length / total,
    speed: speedForSegment(seg.length, avg, globalPace),
    startNorm: seg.startNorm,
    endNorm: seg.endNorm,
    lengthPx: seg.length,
  }));

  // Normalize percentages to sum ~1.0
  const pctSum = segments.reduce((s, e) => s + e.percentage, 0);
  return segments.map((seg) => ({
    ...seg,
    percentage: Math.round((seg.percentage / pctSum) * 100) / 100,
  }));
}

/**
 * Analyze drawn path into Kling multi-shot segments.
 * Geometry is NEVER truncated: when over the shot limit, adjacent segments are
 * merged so S→E still covers the full route; speed adapts to `durationSeconds`.
 */
export function analyzeFlightPath(
  points: Point2D[],
  canvasSize: { width: number; height: number },
  approachPoint?: Point2D | null,
  maxSegments: number = MAX_SEGMENTS,
  durationSeconds: number = 5,
): FlightPathAnalysis {
  const { width, height } = canvasSize;
  const segmentCap = Math.min(MAX_SEGMENTS, Math.max(2, maxSegments));
  const defaultSpeed = paceForPathDuration(0, canvasSize, durationSeconds);
  if (width <= 0 || height <= 0 || points.length < 2) {
    return {
      waypoints: [],
      segments: [{ direction: 'forward', percentage: 1, speed: defaultSpeed, startNorm: { x: 0.5, y: 0.5 }, endNorm: { x: 0.5, y: 0.5 }, lengthPx: 0 }],
      totalLengthPx: 0,
      canvasSize,
    };
  }

  const simplified = simplifyPath(points, Math.max(3, Math.min(width, height) * 0.008));
  const waypoints = simplified.map((p) => ({ ...p, norm: normalizePoint(p, width, height) }));

  const rawSegments: RawSegment[] = [];

  // Optional approach leg: fly from approach point to path start
  if (approachPoint) {
    const approachLen = dist(approachPoint, simplified[0]);
    const diagonal = Math.hypot(width, height);
    if (approachLen > diagonal * 0.04) {
      rawSegments.push({
        direction: 'approach',
        length: approachLen,
        startNorm: normalizePoint(approachPoint, width, height),
        endNorm: waypoints[0].norm,
      });
    }
  }

  for (let i = 1; i < simplified.length; i++) {
    const prev = simplified[i - 1];
    const curr = simplified[i];
    const dx = curr.x - prev.x;
    const dy = curr.y - prev.y;
    const len = Math.hypot(dx, dy);
    if (len === 0) continue;

    const startNorm = normalizePoint(prev, width, height);
    const endNorm = normalizePoint(curr, width, height);
    const direction = classifySegment(startNorm, endNorm, dx, dy);

    const last = rawSegments[rawSegments.length - 1];
    if (last && last.direction === direction && direction !== 'approach') {
      last.length += len;
      last.endNorm = endNorm;
    } else {
      rawSegments.push({ direction, length: len, startNorm, endNorm });
    }
  }

  if (rawSegments.length === 0) {
    return {
      waypoints,
      segments: [{
        direction: 'forward',
        percentage: 1,
        speed: defaultSpeed,
        startNorm: waypoints[0].norm,
        endNorm: waypoints[waypoints.length - 1].norm,
        lengthPx: 0,
      }],
      totalLengthPx: 0,
      canvasSize,
    };
  }

  const totalLength = rawSegments.reduce((s, e) => s + e.length, 0);
  const merged = mergeSmallSegments(rawSegments, totalLength);
  // Merge only — never drop the end of the path
  const capped = capSegments(merged, segmentCap);
  const segments = toWeightedSegments(capped, canvasSize, durationSeconds);

  return { waypoints, segments, totalLengthPx: totalLength, canvasSize };
}

/** Draw direction arrows along a path on a canvas context. */
export function drawPathOverlay(
  ctx: CanvasRenderingContext2D,
  points: Point2D[],
  approachPoint: Point2D | null,
  options?: { showMarkers?: boolean; showArrows?: boolean },
) {
  const showMarkers = options?.showMarkers !== false;
  const showArrows = options?.showArrows !== false;

  if (approachPoint && points.length > 0) {
    ctx.save();
    ctx.strokeStyle = 'rgba(34, 211, 238, 0.85)';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 6]);
    ctx.beginPath();
    ctx.moveTo(approachPoint.x, approachPoint.y);
    ctx.lineTo(points[0].x, points[0].y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = '#22d3ee';
    ctx.beginPath();
    ctx.arc(approachPoint.x, approachPoint.y, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 9px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('A', approachPoint.x, approachPoint.y);
    ctx.restore();
  }

  if (points.length > 1) {
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = Math.max(2, ctx.lineWidth || 3);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.stroke();

    if (showArrows) {
      // Direction arrows every ~45px along path
      let distUntilNextArrow = 20;
      const arrowSpacing = 45;
      for (let i = 1; i < points.length; i++) {
        const p0 = points[i - 1];
        const p1 = points[i];
        const segLen = dist(p0, p1);
        if (segLen === 0) continue;

        let cursor = distUntilNextArrow;
        while (cursor <= segLen) {
          const t = cursor / segLen;
          const ax = p0.x + (p1.x - p0.x) * t;
          const ay = p0.y + (p1.y - p0.y) * t;
          const angle = Math.atan2(p1.y - p0.y, p1.x - p0.x);
          drawArrowHead(ctx, ax, ay, angle);
          cursor += arrowSpacing;
        }
        distUntilNextArrow = cursor - segLen;
      }
    }
  }

  if (showMarkers && points.length > 0) {
    const start = points[0];
    const end = points[points.length - 1];

    ctx.fillStyle = '#22c55e';
    ctx.beginPath();
    ctx.arc(start.x, start.y, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 9px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('S', start.x, start.y);

    if (points.length > 1) {
      ctx.fillStyle = '#ef4444';
      ctx.beginPath();
      ctx.arc(end.x, end.y, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = '#fff';
      ctx.fillText('E', end.x, end.y);
    }
  }
}

function drawArrowHead(ctx: CanvasRenderingContext2D, x: number, y: number, angle: number) {
  const size = 7;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.fillStyle = 'rgba(251, 191, 36, 0.95)';
  ctx.beginPath();
  ctx.moveTo(size, 0);
  ctx.lineTo(-size * 0.6, size * 0.55);
  ctx.lineTo(-size * 0.6, -size * 0.55);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/** Render path overlay onto a merged export canvas (full resolution). */
export function drawPathOnImage(
  ctx: CanvasRenderingContext2D,
  points: Point2D[],
  approachPoint: Point2D | null,
  displaySize: { width: number; height: number },
  options?: { showMarkers?: boolean; showArrows?: boolean },
) {
  const scaleX = ctx.canvas.width / displaySize.width;
  const scaleY = ctx.canvas.height / displaySize.height;
  const scaled = points.map((p) => ({ x: p.x * scaleX, y: p.y * scaleY }));
  const scaledApproach = approachPoint
    ? { x: approachPoint.x * scaleX, y: approachPoint.y * scaleY }
    : null;

  ctx.save();
  ctx.lineWidth = Math.max(3, 4 * Math.max(scaleX, scaleY));
  drawPathOverlay(ctx, scaled, scaledApproach, options);
  ctx.restore();
}

/**
 * Downsample stroke for export while preserving shape (same polyline the user drew).
 * Keeps endpoints; drops near-duplicate points only.
 */
export function densifyStrokeForExport(points: Point2D[], minSpacingPx = 2): Point2D[] {
  if (points.length <= 2) return points.slice();
  const out: Point2D[] = [points[0]];
  for (let i = 1; i < points.length - 1; i++) {
    const prev = out[out.length - 1];
    const cur = points[i];
    if (dist(prev, cur) >= minSpacingPx) out.push(cur);
  }
  out.push(points[points.length - 1]);
  return out;
}

export const SCENE_COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#06b6d4', '#a855f7', '#ec4899'];

export type FlightScene = {
  points: Point2D[];
  direction: FlightSegmentKind;
  speed: FlightSpeed;
  percentage: number;
  startNorm: NormalizedPoint;
  endNorm: NormalizedPoint;
  bearing: string;
  lengthPx: number;
  start_frame_b64?: string;
  end_frame_b64?: string;
};

export type PathAnchorFrames = {
  startB64: string;
  endB64: string;
  startNorm: NormalizedPoint;
  endNorm: NormalizedPoint;
  direction: FlightSegmentKind;
  bearing: string;
  lengthPx: number;
  speed: FlightSpeed;
};

/** One drawn line → one camera move (S→E), no auto-split into legs. */
export function analyzeSceneSingleMove(
  points: Point2D[],
  canvasSize: { width: number; height: number },
  durationSeconds: number,
  approachPoint?: Point2D | null,
): Omit<FlightScene, 'points' | 'start_frame_b64' | 'end_frame_b64'> {
  const { width, height } = canvasSize;
  const empty = {
    direction: 'forward' as FlightSegmentKind,
    speed: paceForPathDuration(0, canvasSize, durationSeconds),
    percentage: 1,
    startNorm: { x: 0.5, y: 0.5 },
    endNorm: { x: 0.5, y: 0.5 },
    bearing: 'RIGHT',
    lengthPx: 0,
  };
  if (width <= 0 || height <= 0 || points.length < 2) return empty;

  const simplified = simplifyPath(points, Math.max(3, Math.min(width, height) * 0.008));
  if (simplified.length < 2) return empty;

  let start = simplified[0];
  const end = simplified[simplified.length - 1];
  let lengthPx = 0;
  for (let i = 1; i < simplified.length; i++) {
    lengthPx += dist(simplified[i - 1], simplified[i]);
  }

  if (approachPoint) {
    const approachLen = dist(approachPoint, start);
    if (approachLen > Math.hypot(width, height) * 0.04) {
      lengthPx += approachLen;
      start = approachPoint;
    }
  }

  const startNorm = normalizePoint(start, width, height);
  const endNorm = normalizePoint(end, width, height);
  const dx = endNorm.x - startNorm.x;
  const dy = endNorm.y - startNorm.y;
  const direction = classifySegment(startNorm, endNorm, dx * width, dy * height);
  const bearing = bearingLabel(dx, dy);
  const speed = paceForPathDuration(lengthPx, canvasSize, durationSeconds);

  return {
    direction,
    speed,
    percentage: 1,
    startNorm,
    endNorm,
    bearing,
    lengthPx,
  };
}

/** Build weighted FlightScene[] from committed lines (+ optional in-progress line). */
export function buildFlightScenes(
  scenes: Point2D[][],
  canvasSize: { width: number; height: number },
  durationSeconds: number,
  approachPoint?: Point2D | null,
  currentPoints?: Point2D[],
): FlightScene[] {
  const lines: Point2D[][] = [
    ...scenes.filter((s) => s.length >= 2),
    ...(currentPoints && currentPoints.length >= 2 ? [currentPoints] : []),
  ].slice(0, MAX_SEGMENTS);

  if (lines.length === 0) return [];

  const analyzed = lines.map((pts, idx) => {
    const meta = analyzeSceneSingleMove(
      pts,
      canvasSize,
      durationSeconds,
      idx === 0 ? approachPoint : null,
    );
    return { points: pts, ...meta };
  });

  const totalLen = analyzed.reduce((s, e) => s + Math.max(e.lengthPx, 1), 0) || 1;
  return analyzed.map((s) => ({
    ...s,
    percentage: Math.max(0.01, s.lengthPx / totalLen),
  }));
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image for path anchors'));
    img.src = src;
  });
}

/**
 * Crop/reframe the source photo around a focus point.
 * Smaller cropScale = tighter framing (camera closer / end of travel).
 */
export function cropAroundFocus(
  img: HTMLImageElement,
  focusNorm: NormalizedPoint,
  cropScale: number,
): string {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const scale = Math.max(0.35, Math.min(1, cropScale));
  const cropW = w * scale;
  const cropH = h * scale;
  let x = focusNorm.x * w - cropW / 2;
  let y = focusNorm.y * h - cropH / 2;
  x = Math.max(0, Math.min(w - cropW, x));
  y = Math.max(0, Math.min(h - cropH, y));

  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  if (!ctx) return '';
  ctx.drawImage(img, x, y, cropW, cropH, 0, 0, w, h);
  return out.toDataURL('image/jpeg', 0.92);
}

/**
 * Geometric first/end frames for a path: wider crop at S, tighter/shifted crop at E.
 */
export async function buildPathAnchorFrames(
  imageSrc: string,
  points: Point2D[],
  displaySize: { width: number; height: number },
  durationSeconds: number,
  approachPoint?: Point2D | null,
): Promise<PathAnchorFrames | null> {
  const meta = analyzeSceneSingleMove(points, displaySize, durationSeconds, approachPoint);
  if (meta.lengthPx <= 0 && points.length < 2) return null;

  const img = await loadImage(imageSrc);
  const travel = Math.hypot(meta.endNorm.x - meta.startNorm.x, meta.endNorm.y - meta.startNorm.y);
  const startScale = 0.92;
  const endScale = Math.max(0.45, 0.92 - travel * 0.55);

  return {
    startB64: cropAroundFocus(img, meta.startNorm, startScale),
    endB64: cropAroundFocus(img, meta.endNorm, endScale),
    startNorm: meta.startNorm,
    endNorm: meta.endNorm,
    direction: meta.direction,
    bearing: meta.bearing,
    lengthPx: meta.lengthPx,
    speed: meta.speed,
  };
}

/** Draw committed scenes + current stroke with distinct colors. */
export function drawScenesOverlay(
  ctx: CanvasRenderingContext2D,
  scenes: Point2D[][],
  currentPoints: Point2D[],
  approachPoint: Point2D | null,
) {
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  if (approachPoint && (scenes[0]?.length || currentPoints.length)) {
    const first = scenes[0]?.[0] || currentPoints[0];
    if (first) {
      ctx.save();
      ctx.strokeStyle = 'rgba(34, 211, 238, 0.85)';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 6]);
      ctx.beginPath();
      ctx.moveTo(approachPoint.x, approachPoint.y);
      ctx.lineTo(first.x, first.y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#22d3ee';
      ctx.beginPath();
      ctx.arc(approachPoint.x, approachPoint.y, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 9px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('A', approachPoint.x, approachPoint.y);
      ctx.restore();
    }
  }

  const drawColoredPath = (pts: Point2D[], color: string, indexLabel: number, showSE: boolean) => {
    if (pts.length < 1) return;
    if (pts.length > 1) {
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
      ctx.stroke();
    }
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(pts[0].x, pts[0].y, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 9px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(showSE ? 'S' : String(indexLabel), pts[0].x, pts[0].y);
    if (pts.length > 1) {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(pts[pts.length - 1].x, pts[pts.length - 1].y, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.fillText(showSE ? 'E' : String(indexLabel), pts[pts.length - 1].x, pts[pts.length - 1].y);
    }
  };

  scenes.forEach((pts, i) => {
    if (pts.length >= 2) {
      drawColoredPath(pts, SCENE_COLORS[i % SCENE_COLORS.length], i + 1, scenes.length === 1 && currentPoints.length < 2);
    }
  });

  if (currentPoints.length >= 1) {
    const idx = scenes.length;
    drawColoredPath(
      currentPoints,
      SCENE_COLORS[idx % SCENE_COLORS.length],
      idx + 1,
      scenes.length === 0,
    );
  }
}
